import React, { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown, Inbox } from 'lucide-react';

/**
 * 📊 DataGridEnterprise — Premium data grid for all enterprise modules
 *
 * Visual signature:
 *   - enterprise-table-shell container (border + radius + bg)
 *   - enterprise-table-header (sticky, uppercase, mono, muted)
 *   - enterprise-table-row (hover → surface-3, selected → left indicator)
 *   - 40px row height, 16px cell padding
 *   - Built-in column sorting with visual indicators
 *   - Hover row actions slot
 *   - Premium empty state
 */

export type SortDirection = 'asc' | 'desc';

export interface DataGridColumn<T> {
  key: string;
  label: string;
  /** Render cell content. Defaults to `String(row[key])` */
  render?: (row: T, index: number) => React.ReactNode;
  /** Header alignment */
  align?: 'left' | 'center' | 'right';
  /** Column width hint (css) */
  width?: string;
  /** Use monospace font for this column (numbers, codes, dates) */
  mono?: boolean;
  /** Enable sorting on this column */
  sortable?: boolean;
}

interface DataGridEnterpriseProps<T> {
  columns: DataGridColumn<T>[];
  data: T[];
  /** Unique key extractor */
  rowKey: (row: T) => string;
  isLoading?: boolean;
  /** Premium empty state icon (defaults to Inbox) */
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptySubtitle?: string;
  /** Optional CTA button in empty state */
  emptyAction?: React.ReactNode;
  selectedKey?: string | null;
  onRowClick?: (row: T) => void;
  onRowDoubleClick?: (row: T) => void;
  onRowContextMenu?: (row: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  /** Render hover actions for a row (appears at right edge on hover) */
  rowActions?: (row: T) => React.ReactNode;
  /** Controlled sort */
  sortField?: string;
  sortDirection?: SortDirection;
  onSort?: (field: string, direction: SortDirection) => void;
  /** Optional footer left text override (e.g. custom text). If not provided, shows record count. */
  footerText?: string;
  /** Hide the auto-generated footer entirely */
  hideFooter?: boolean;
  /** Optional per-row class (e.g. health / overdue tint) */
  getRowClassName?: (row: T) => string;
  className?: string;
}

export function DataGridEnterprise<T>({
  columns,
  data,
  rowKey,
  isLoading,
  emptyIcon,
  emptyTitle = 'Nenhum registro encontrado',
  emptySubtitle,
  emptyAction,
  selectedKey,
  onRowClick,
  onRowDoubleClick,
  onRowContextMenu,
  rowActions,
  sortField,
  sortDirection,
  onSort,
  footerText,
  hideFooter = false,
  getRowClassName,
  className = '',
}: DataGridEnterpriseProps<T>) {

  // Internal sort state if not controlled
  const [internalSort, setInternalSort] = useState<{ field: string; dir: SortDirection } | null>(null);

  const activeSortField = sortField ?? internalSort?.field;
  const activeSortDir = sortDirection ?? internalSort?.dir ?? 'asc';

  const handleSort = useCallback((field: string) => {
    if (onSort) {
      const newDir = activeSortField === field && activeSortDir === 'asc' ? 'desc' : 'asc';
      onSort(field, newDir);
    } else {
      setInternalSort(prev =>
        prev?.field === field
          ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
          : { field, dir: 'asc' }
      );
    }
  }, [onSort, activeSortField, activeSortDir]);

  // Internal sorting (when not controlled)
  const sortedData = React.useMemo(() => {
    if (onSort || !internalSort) return data;
    const { field, dir } = internalSort;
    return [...data].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[field];
      const bVal = (b as Record<string, unknown>)[field];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = String(aVal).localeCompare(String(bVal), 'pt-BR', { numeric: true });
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [data, internalSort, onSort]);

  /* ─── Loading skeleton ─── */
  if (isLoading) {
    return (
      <div className="enterprise-table-shell">
        <table className="min-w-full table-fixed text-[11px]">
          <thead className="enterprise-table-header text-left font-mono text-[10px] uppercase tracking-[0.14em]">
            <tr>
              {columns.map(col => (
                <th key={col.key} className="px-4 py-2 font-medium" style={col.width ? { width: col.width } : undefined}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className="enterprise-table-row animate-pulse">
                <td colSpan={columns.length} className="px-4" style={{ height: 40 }}>
                  <div className="h-3 rounded-[2px] enterprise-surface-3" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  /* ─── Premium empty state ─── */
  if (data.length === 0) {
    return (
      <div className="enterprise-card-surface flex min-h-[260px] flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="enterprise-text-muted" style={{ opacity: 0.5 }}>
          {emptyIcon ?? <Inbox size={40} strokeWidth={1} />}
        </div>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] enterprise-text-muted">{emptyTitle}</p>
          {emptySubtitle && <p className="mt-1 text-xs enterprise-text-secondary">{emptySubtitle}</p>}
        </div>
        {emptyAction && <div className="mt-2">{emptyAction}</div>}
      </div>
    );
  }

  const displayData = onSort ? data : sortedData;
  const footer = footerText ?? `${data.length} ${data.length === 1 ? 'registro' : 'registros'}`;

  return (
    <div className={`enterprise-table-shell overflow-x-auto ${className}`.trim()}>
      <table className="min-w-full table-fixed text-[11px]">
        <thead className="enterprise-table-header text-left font-mono text-[10px] uppercase tracking-[0.14em]">
          <tr>
            {columns.map(col => {
              const isSorted = activeSortField === col.key;
              return (
                <th
                  key={col.key}
                  className={`px-4 py-2 font-medium select-none ${
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                  } ${col.sortable ? 'cursor-pointer hover:enterprise-text-primary' : ''}`}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.sortable && isSorted && (
                      activeSortDir === 'asc'
                        ? <ChevronUp size={11} className="enterprise-text-primary" />
                        : <ChevronDown size={11} className="enterprise-text-primary" />
                    )}
                  </span>
                </th>
              );
            })}
            {/* Spacer for row actions column */}
            {rowActions && <th className="w-10" />}
          </tr>
        </thead>
        <tbody>
          {displayData.map((row, idx) => {
            const key = rowKey(row);
            const isSelected = selectedKey === key;
            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
                onContextMenu={onRowContextMenu ? (event) => {
                  event.preventDefault();
                  onRowContextMenu(row, event);
                } : undefined}
                className={`enterprise-table-row group ${onRowClick ? 'cursor-pointer' : ''} ${isSelected ? 'is-selected' : ''} ${getRowClassName?.(row) ?? ''}`.trim()}
                style={{ height: 40 }}
              >
                {columns.map((col, colIdx) => (
                  <td
                    key={col.key}
                    className={`px-4 py-2 align-middle ${col.mono ? 'font-mono' : ''} ${
                      colIdx === 0 && isSelected
                        ? 'relative pl-5 enterprise-text-primary font-semibold before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-[#718198] before:shadow-[0_0_6px_rgba(113,129,152,0.28)]'
                        : colIdx === 0
                          ? 'enterprise-text-primary font-medium'
                          : 'enterprise-text-secondary'
                    } ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                  >
                    {col.render ? col.render(row, idx) : String((row as Record<string, unknown>)[col.key] ?? '—')}
                  </td>
                ))}
                {/* Hover actions */}
                {rowActions && (
                  <td className="px-2 py-2 text-right align-middle">
                    <div className="invisible group-hover:visible">
                      {rowActions(row)}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!hideFooter && (
        <div className="border-t border-zinc-800/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.12em] enterprise-text-muted">
          {footer}
        </div>
      )}
    </div>
  );
}

export default DataGridEnterprise;
